import _slicedToArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useCallback, useEffect, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
var SCROLL_EDGE_PX = 120;

var findScrollableParent = function findScrollableParent(node) {
  var _node$parentElement;

  var current = (_node$parentElement = node === null || node === void 0 ? void 0 : node.parentElement) !== null && _node$parentElement !== void 0 ? _node$parentElement : null;

  while (current && current !== document.body) {
    var _window$getComputedSt = window.getComputedStyle(current),
        overflowY = _window$getComputedSt.overflowY;

    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight + 1) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

var isNearScrollEdge = function isNearScrollEdge(container, clientY) {
  var edgePx = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : SCROLL_EDGE_PX;
  var rect = container.getBoundingClientRect();
  return clientY - rect.top < edgePx || rect.bottom - clientY < edgePx;
};

export var DND_ITEM_TYPE_QUESTION = 'QUESTION_ITEM';
/** Center editor cards (questions + headings). Kept separate so sidebar ↔ center don't cross-hover. */

export var DND_ITEM_TYPE_CENTER_FORM_ITEM = 'CENTER_FORM_ITEM';
/** Answer options inside a question card. */

export var DND_ITEM_TYPE_ANSWER = 'ANSWER_ITEM';
export function useQuestionDragAndDrop(_ref) {
  var targetItem = _ref.targetItem,
      index = _ref.index,
      moveQuestion = _ref.moveQuestion,
      onDragEnd = _ref.onDragEnd,
      onCrossSectionDrop = _ref.onCrossSectionDrop,
      onCrossSectionHover = _ref.onCrossSectionHover,
      onDragBegin = _ref.onDragBegin,
      title = _ref.title,
      _ref$canDrag = _ref.canDrag,
      canDrag = _ref$canDrag === void 0 ? true : _ref$canDrag,
      _ref$liveReorder = _ref.liveReorder,
      liveReorder = _ref$liveReorder === void 0 ? true : _ref$liveReorder,
      _ref$itemType = _ref.itemType,
      itemType = _ref$itemType === void 0 ? DND_ITEM_TYPE_QUESTION : _ref$itemType,
      sectionId = _ref.sectionId,
      _ref$acceptCrossSecti = _ref.acceptCrossSection,
      acceptCrossSection = _ref$acceptCrossSecti === void 0 ? true : _ref$acceptCrossSecti,
      itemKind = _ref.itemKind;
  var rowNodeRef = useRef(null);
  var indexRef = useRef(index);
  var moveQuestionRef = useRef(moveQuestion);
  var onDragEndRef = useRef(onDragEnd);
  var onCrossSectionDropRef = useRef(onCrossSectionDrop);
  var onCrossSectionHoverRef = useRef(onCrossSectionHover);
  var onDragBeginRef = useRef(onDragBegin);
  var titleRef = useRef(title);
  var targetIdRef = useRef(targetItem.id.toString());
  var liveReorderRef = useRef(liveReorder);
  var canDragRef = useRef(canDrag);
  var sectionIdRef = useRef(sectionId);
  var acceptCrossSectionRef = useRef(acceptCrossSection);
  var itemKindRef = useRef(itemKind);
  indexRef.current = index;
  moveQuestionRef.current = moveQuestion;
  onDragEndRef.current = onDragEnd;
  onCrossSectionDropRef.current = onCrossSectionDrop;
  onCrossSectionHoverRef.current = onCrossSectionHover;
  onDragBeginRef.current = onDragBegin;
  titleRef.current = title;
  targetIdRef.current = targetItem.id.toString();
  liveReorderRef.current = liveReorder;
  canDragRef.current = canDrag;
  sectionIdRef.current = sectionId;
  acceptCrossSectionRef.current = acceptCrossSection;
  itemKindRef.current = itemKind;

  var _useDrop = useDrop({
    accept: itemType,
    canDrop: function canDrop(item) {
      var targetSectionId = sectionIdRef.current;

      if (!targetSectionId || item.sectionId === targetSectionId) {
        return true;
      }

      if (!acceptCrossSectionRef.current) {
        return false;
      }

      return true;
    },
    hover: function hover(item, monitor) {
      var _rowNodeRef$current;

      if (!liveReorderRef.current) return;
      var targetSectionId = sectionIdRef.current;
      var hoverIndex = indexRef.current;
      var hoverBoundingRect = (_rowNodeRef$current = rowNodeRef.current) === null || _rowNodeRef$current === void 0 ? void 0 : _rowNodeRef$current.getBoundingClientRect();
      if (!hoverBoundingRect) return;
      var hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      var clientOffset = monitor.getClientOffset();
      if (!clientOffset) return; // Pause live reorder while auto-scrolling a nested list — otherwise
      // inserting the ghost fights scrollTop and the list stutters.

      // Pause live reorder while auto-scrolling a nested list — otherwise
      // inserting the ghost fights scrollTop and the list stutters.
      var scrollParent = findScrollableParent(rowNodeRef.current);

      if (scrollParent && isNearScrollEdge(scrollParent, clientOffset.y)) {
        return;
      }

      var hoverClientY = clientOffset.y - hoverBoundingRect.top; // Foreign section → live-insert into this list (gap animation).

      // Foreign section → live-insert into this list (gap animation).
      if (targetSectionId && item.sectionId !== targetSectionId && acceptCrossSectionRef.current) {
        var _onCrossSectionHoverR;

        // Crossing into a new list: always insert at the hovered row once the
        // pointer is over it (midpoint gating uses stale dragIndex otherwise).
        (_onCrossSectionHoverR = onCrossSectionHoverRef.current) === null || _onCrossSectionHoverR === void 0 ? void 0 : _onCrossSectionHoverR.call(onCrossSectionHoverRef, item, hoverIndex);
        item.sectionId = targetSectionId;
        item.index = hoverIndex;
        return;
      }

      var dragIndex = item.index;
      if (dragIndex === hoverIndex) return; // Only shift once the pointer crosses the vertical midpoint (avoids jitter).

      // Only shift once the pointer crosses the vertical midpoint (avoids jitter).
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveQuestionRef.current(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: function drop(item, monitor) {
      if (monitor.didDrop()) {
        return;
      }

      var targetSectionId = sectionIdRef.current;
      var hoverIndex = indexRef.current; // Always prefer originalSectionId — item.sectionId is mutated during hover.

      // Always prefer originalSectionId — item.sectionId is mutated during hover.
      var sourceSectionId = item.originalSectionId;

      if (targetSectionId && sourceSectionId && sourceSectionId !== targetSectionId && acceptCrossSectionRef.current) {
        if (!onCrossSectionDropRef.current) {
          return {
            dropped: false
          };
        } // Prefer the live preview index carried on the drag item.


        // Prefer the live preview index carried on the drag item.
        var targetIndex = item.sectionId === targetSectionId ? item.index : hoverIndex;
        return onCrossSectionDropRef.current(item, targetIndex) || {
          dropped: true,
          crossSection: true,
          targetSectionId: targetSectionId,
          targetIndex: targetIndex
        };
      }

      if (liveReorderRef.current) {
        return {
          dropped: true
        };
      }

      var dragIndex = item.index;
      if (dragIndex === hoverIndex) return {
        dropped: true
      };
      moveQuestionRef.current(dragIndex, hoverIndex);
      item.index = hoverIndex;
      return {
        dropped: true
      };
    }
  }),
      _useDrop2 = _slicedToArray(_useDrop, 2),
      drop = _useDrop2[1];

  var _useDrag = useDrag({
    type: itemType,
    item: function item() {
      var _onDragBeginRef$curre;

      var nextItem = {
        id: targetIdRef.current,
        index: indexRef.current,
        originalIndex: indexRef.current,
        title: titleRef.current || 'Untitled Question',
        sectionId: sectionIdRef.current || '',
        originalSectionId: sectionIdRef.current || '',
        itemKind: itemKindRef.current
      };
      (_onDragBeginRef$curre = onDragBeginRef.current) === null || _onDragBeginRef$curre === void 0 ? void 0 : _onDragBeginRef$curre.call(onDragBeginRef, nextItem);
      return nextItem;
    },
    canDrag: function canDrag() {
      return canDragRef.current;
    },
    end: function end(item, monitor) {
      var _onDragEndRef$current;

      if (!item) return;
      var dropResult = monitor.getDropResult();
      (_onDragEndRef$current = onDragEndRef.current) === null || _onDragEndRef$current === void 0 ? void 0 : _onDragEndRef$current.call(onDragEndRef, item, dropResult, monitor.didDrop());
    },
    collect: function collect(monitor) {
      return {
        isDragging: monitor.isDragging()
      };
    }
  }),
      _useDrag2 = _slicedToArray(_useDrag, 3),
      isDragging = _useDrag2[0].isDragging,
      drag = _useDrag2[1],
      preview = _useDrag2[2];

  useEffect(function () {
    preview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [preview]); // Callback connectors are more reliable than RefObjects with conditional children.

  var dropRef = useCallback(function (node) {
    rowNodeRef.current = node;
    drop(node);
  }, [drop]);
  var dragRef = useCallback(function (node) {
    drag(node);
  }, [drag]);
  return {
    dropRef: dropRef,
    dragRef: dragRef,
    isDragging: isDragging
  };
}
/** @deprecated Prefer dropRef / dragRef from useQuestionDragAndDrop. */